import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { CVAnalyser, initEvidence, SPECIALTIES } from "../App";
import CvMagicLinkGate, { PENDING_OPTIN_KEY } from "../components/CvMagicLinkGate";

const CV_SPECIALTIES = ["anaesthetics", "emergency_medicine"];

export default function CvAnalyserPage() {
  const [session, setSession] = useState(undefined);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => setSession(s));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);

  if (session === undefined) {
    return (
      <div style={{ minHeight: "100vh", background: "#0a1628", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div
          style={{
            width: 28,
            height: 28,
            border: "3px solid #1e3a5f",
            borderTopColor: "#2FAF9F",
            borderRadius: "50%",
            animation: "cvspin 1s linear infinite",
          }}
        />
        <style>{`@keyframes cvspin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (!session) return <CvMagicLinkGate />;

  return <CvRouteShell user={session.user} />;
}

function CvRouteShell({ user }) {
  const uid = user.id.replace(/-/g, "").slice(0, 12);
  const LS_SP = `cv_route_sp_${uid}`;

  const [specialty, setSpecialty] = useState(() => {
    try {
      const v = localStorage.getItem(LS_SP);
      if (v && SPECIALTIES[v]) return v;
    } catch {
      /* ignore */
    }
    return "anaesthetics";
  });

  const [evidence, setEvidence] = useState(() => initEvidence(specialty));
  const [hydrated, setHydrated] = useState(false);
  const syncTimer = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(LS_SP, specialty);
    } catch {
      /* ignore */
    }
  }, [specialty, LS_SP]);

  // Flush any pending marketing opt-in that was captured before auth
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PENDING_OPTIN_KEY);
      if (!raw) return;
      const pending = JSON.parse(raw);
      if (pending.email !== user.email.toLowerCase()) return;
      supabase
        .from("profiles")
        .upsert(
          {
            id: user.id,
            marketing_opt_in: pending.optIn,
            marketing_opt_in_at: pending.optIn ? pending.ts : null,
          },
          { onConflict: "id" },
        )
        .then(() => {
          localStorage.removeItem(PENDING_OPTIN_KEY);
        });
    } catch { /* ignore */ }
  }, [user.id, user.email]);

  useEffect(() => {
    if (syncTimer.current) {
      clearTimeout(syncTimer.current);
      syncTimer.current = null;
    }
    setHydrated(false);
    setEvidence(initEvidence(specialty));
    supabase
      .from("user_evidence")
      .select("evidence_json")
      .eq("user_id", user.id)
      .eq("specialty", specialty)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!error && data?.evidence_json) {
          setEvidence({ ...initEvidence(specialty), ...data.evidence_json });
        }
        setHydrated(true);
      });
  }, [user.id, specialty]);

  const onUpdate = useCallback(
    (itemId, updates) => {
      setEvidence(curr => {
        const next = { ...curr, [itemId]: { ...(curr[itemId] || {}), ...updates } };
        if (syncTimer.current) clearTimeout(syncTimer.current);
        syncTimer.current = setTimeout(() => {
          supabase.from("user_evidence").upsert(
            {
              user_id: user.id,
              specialty,
              evidence_json: next,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id,specialty" },
          );
        }, 2000);
        return next;
      });
    },
    [user.id, specialty],
  );

  if (!hydrated) {
    return (
      <div style={{ minHeight: "100vh", background: "#F2F4F7", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div
          style={{
            width: 28,
            height: 28,
            border: "3px solid #E2E8F0",
            borderTopColor: "#2FAF9F",
            borderRadius: "50%",
            animation: "cvspin2 1s linear infinite",
          }}
        />
        <style>{`@keyframes cvspin2{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#F2F4F7", fontFamily: "'Inter',system-ui,sans-serif", padding: "24px 16px 48px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=DM+Serif+Display&display=swap');`}</style>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#2FAF9F", letterSpacing: ".12em", textTransform: "uppercase" }}>Free tool</div>
            <div style={{ fontFamily: "'DM Serif Display',Georgia,serif", fontSize: 22, fontWeight: 700, color: "#0F1F2E" }}>CV analysis</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <label style={{ fontSize: 12, color: "#64748B", display: "flex", alignItems: "center", gap: 8 }}>
              Specialty
              <select
                value={specialty}
                onChange={e => setSpecialty(e.target.value)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid #E2E8F0",
                  background: "#fff",
                  color: "#0F1F2E",
                  fontSize: 13,
                  fontFamily: "inherit",
                }}
              >
                {CV_SPECIALTIES.map(id => (
                  <option key={id} value={id}>
                    {SPECIALTIES[id].name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => supabase.auth.signOut()}
              style={{
                fontSize: 12,
                color: "#64748B",
                background: "none",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
                fontFamily: "inherit",
              }}
            >
              Sign out
            </button>
          </div>
        </div>
        <CVAnalyser specialty={specialty} evidence={evidence} onUpdate={onUpdate} themeMode="clinical_light" cvFileData={null} />
      </div>
    </div>
  );
}
